/**
 * CRA adapter: where the AI support requests go and which headers the
 * streaming `fetch` sends. The RTK Query endpoints (src/_api/aiSupportApi.js)
 * and `POST /chat` (core/streamChat) both resolve their URLs here, so the dev
 * mock override moves them together. next-mapit rewrites only this file.
 */

import { $BASE_URL } from "src/constants";

export { newClientMessageId } from "src/_modules/AiSupportChat/core/ids";

const API_PREFIX = "/api/v1";
const AI_SUPPORT_PREFIX = "/ai-support";
const AI_SUPPORT_ADMIN_PREFIX = "/admin-panel/ai-support";

/**
 * Origin of the local mock (`scripts/ai-support-mock`), or null.
 *
 * Read on every call, not at import, so tests can toggle it. Ignored in
 * production as a second guard: `yarn build` never reads
 * `.env.development.local`, where the variable lives (C21).
 *
 * @returns {string | null}
 */
const getMockOrigin = () => {
  if (process.env.NODE_ENV === "production") return null;
  const origin = process.env.REACT_APP_AI_SUPPORT_MOCK_URL;
  return origin ? origin.replace(/\/+$/, "") : null;
};

/**
 * URL of an AI support JSON endpoint, in the form RTK Query expects:
 * relative to the apiSlice base (`${$BASE_URL}/api/v1`) normally, absolute
 * when the mock is on (fetchBaseQuery passes absolute URLs through).
 *
 * @param {string} [path] e.g. `/eligibility`, `/conversations/<id>`
 * @returns {string}
 */
export const aiSupportUrl = (path = "") => {
  const mockOrigin = getMockOrigin();
  return mockOrigin
    ? `${mockOrigin}${API_PREFIX}${AI_SUPPORT_PREFIX}${path}`
    : `${AI_SUPPORT_PREFIX}${path}`;
};

/**
 * URL of an AI-developer admin endpoint (src/_api/aiSupportAdminApi.js),
 * with the same mock rule as `aiSupportUrl`.
 *
 * @param {string} [path] e.g. `/config`, `/profiles/<id>/test`
 * @returns {string}
 */
export const aiSupportAdminUrl = (path = "") => {
  const mockOrigin = getMockOrigin();
  return mockOrigin
    ? `${mockOrigin}${API_PREFIX}${AI_SUPPORT_ADMIN_PREFIX}${path}`
    : `${AI_SUPPORT_ADMIN_PREFIX}${path}`;
};

/**
 * Absolute URL of the SSE endpoint `POST /ai-support/chat`.
 *
 * @returns {string}
 */
export const getChatUrl = () =>
  `${getMockOrigin() || $BASE_URL}${API_PREFIX}${AI_SUPPORT_PREFIX}/chat`;

/**
 * Headers for `POST /chat`: the JSON body + SSE accept, then the same token,
 * language and timezone rules as `prepareHeaders` in src/_api/apiSlice.js.
 * Keep the two in sync.
 *
 * @returns {Record<string, string>}
 */
export const getChatHeaders = () => {
  const headers = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
  };
  const token = localStorage.getItem("token") || null;
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  headers["Accept-Language"] = localStorage.getItem("i18nextLng") || "ar-SA";
  headers.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return headers;
};
